import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Hook para manejar el timeout de sesión con advertencia opcional
 * @param {Object} options - Opciones de configuración
 * @param {number} options.timeoutMinutes - Minutos de inactividad antes de cerrar sesión (default: 30)
 * @param {number} options.warningSeconds - Segundos antes del cierre para mostrar advertencia (default: 60)
 * @param {boolean} options.showWarning - Si mostrar o no la advertencia (default: true)
 * @param {function} options.onLogout - Función a ejecutar al cerrar sesión
 * @param {boolean} options.isActive - Si el hook debe estar activo (solo cuando hay sesión)
 */
function useSessionTimeout({
  timeoutMinutes = 30,
  warningSeconds = 60,
  showWarning = true,
  onLogout,
  isActive = true
}) {
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(warningSeconds);

  const timeoutRef = useRef(null);
  const warningTimeoutRef = useRef(null);
  const countdownRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  const timeoutMs = timeoutMinutes * 60 * 1000;
  const warningMs = warningSeconds * 1000;

  // Limpiar todos los timers
  const clearAllTimers = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
      warningTimeoutRef.current = null;
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  }, []);

  // Cerrar sesión
  const handleLogout = useCallback(() => {
    clearAllTimers();
    setShowWarningModal(false);
    if (onLogout) {
      onLogout();
    }
  }, [clearAllTimers, onLogout]);

  // Iniciar countdown de advertencia
  const startWarningCountdown = useCallback(() => {
    setRemainingSeconds(warningSeconds);
    setShowWarningModal(true);

    countdownRef.current = setInterval(() => {
      setRemainingSeconds((prev) => {
        if (prev <= 1) {
          handleLogout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [warningSeconds, handleLogout]);

  // Resetear el timer de inactividad
  const resetTimer = useCallback(() => {
    if (!isActive) return;

    lastActivityRef.current = Date.now();
    clearAllTimers();
    setShowWarningModal(false);
    setRemainingSeconds(warningSeconds);

    if (showWarning) {
      // Timer para mostrar advertencia
      warningTimeoutRef.current = setTimeout(() => {
        startWarningCountdown();
      }, timeoutMs - warningMs);
    }

    // Timer para cerrar sesión (backup si no hay advertencia o se ignora)
    timeoutRef.current = setTimeout(() => {
      if (!showWarning) {
        handleLogout();
      }
    }, timeoutMs);
  }, [isActive, clearAllTimers, warningSeconds, showWarning, timeoutMs, warningMs, startWarningCountdown, handleLogout]);

  // Extender sesión (cuando el usuario hace clic en "Continuar")
  const extendSession = useCallback(() => {
    setShowWarningModal(false);
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
    resetTimer();
  }, [resetTimer]);

  // Cerrar sesión manualmente desde el modal
  const confirmLogout = useCallback(() => {
    handleLogout();
  }, [handleLogout]);

  // Configurar event listeners para detectar actividad
  useEffect(() => {
    if (!isActive) {
      clearAllTimers();
      setShowWarningModal(false);
      return;
    }

    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];

    const handleActivity = () => {
      // Solo resetear si no está mostrando la advertencia
      if (!showWarningModal) {
        // Throttle: solo resetear si ha pasado al menos 1 segundo
        const now = Date.now();
        if (now - lastActivityRef.current > 1000) {
          resetTimer();
        }
      }
    };

    // Agregar listeners
    events.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    // Iniciar timer
    resetTimer();

    // Cleanup
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
      clearAllTimers();
    };
  }, [isActive, resetTimer, clearAllTimers, showWarningModal]);

  return {
    showWarningModal,
    remainingSeconds,
    extendSession,
    confirmLogout,
    resetTimer
  };
}

export default useSessionTimeout;
